import React, { Component, Fragment } from 'react';
import cn from 'classnames';

import { Alert } from '@lightspeed/flame/Alert';
import Bone from '@lightspeed/cirrus-bone';
import { Button } from '@lightspeed/flame/Button';
import Dialog from '@lightspeed/cirrus-dialog';
import Group from '@lightspeed/cirrus-group';
import { Spinner } from '@lightspeed/flame/Spinner';
import { IconSmallChevronDown } from '@lightspeed/flame/Icon/SmallChevronDown';
import Text, { TextLink } from '@lightspeed/cirrus-text';

import PageActionBar from 'pages/components/PageActionBar';
import Menu, { MenuItem } from 'pages/components/Menu';

const SendMessage = ({ isLoading }) => (
  <Fragment>
    {isLoading && (
      <Alert type="warning" noCloseBtn>
        <Text>
          <Spinner color="orange-200" className="cr-mr-1" /> Another operation is in progress.
          Please wait.
        </Text>
      </Alert>
    )}
    <Text element="div" body>
      <ul>
        <li>Sending a vendor return removes inventory.</li>
        <li>Any corrections will require you to undo the entire return by clicking on reopen.</li>
        <li>Any unsaved changes will be saved automatically upon sending.</li>
      </ul>
    </Text>
  </Fragment>
);

class VendorReturnActionBar extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isDialogOpen: false,
      isSending: false,
    };

    this.handleSend = this.handleSend.bind(this);
    this.handleOpenDialog = this.handleOpenDialog.bind(this);
    this.handleCloseDialog = this.handleCloseDialog.bind(this);
  }

  componentDidUpdate() {
    const { isSubmitting, onSend } = this.props;
    const { isSending } = this.state;

    if (!isSubmitting && isSending) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(
        {
          isSending: false,
        },
        onSend
      );
    }
  }

  handleSend(event) {
    const { dirty, onSubmit } = this.props;

    if (dirty) {
      onSubmit(event);
    }

    this.setState({
      isSending: true,
    });

    this.handleCloseDialog();
  }

  handleOpenDialog() {
    this.setState({
      isDialogOpen: true,
    });
  }

  handleCloseDialog() {
    this.setState({
      isDialogOpen: false,
    });
  }

  render() {
    const {
      vendorReturnID,
      isArchiving,
      isClosing,
      isItemsSaving,
      isLoading,
      isReopening,
      isRestoring,
      isSending,
      isVendorReturnLoading,
      onArchive,
      onClose,
      onReopen,
      onRestore,
      onSend,
      /* formik */
      dirty,
      isSubmitting,
      submitForm,
      onSubmit,
    } = this.props;

    const { isDialogOpen } = this.state;

    return (
      <PageActionBar dirty={dirty} submitForm={submitForm}>
        {onSend && (
          <Dialog
            type="primary"
            title="Send to vendor"
            message={<SendMessage isLoading={isItemsSaving || isVendorReturnLoading} />}
            isOpen={isDialogOpen}
            isLoading={isSubmitting}
            confirmRest={{ disabled: isItemsSaving || isVendorReturnLoading }}
            cancelText="Cancel"
            confirmText="Send"
            onCancel={this.handleCloseDialog}
            onConfirm={this.handleSend}
            className="cirrus"
          />
        )}
        {!isLoading ? (
          <Group className="cr-justify-between cr-width-full">
            {onRestore ? (
              <Group>
                <Text body dimmed>
                  This vendor return is archived.
                  <TextLink
                    onClick={!isRestoring ? onRestore : () => {}}
                    bold
                    className="cr-pl-2"
                    data-test="vendor-return-unarchive-link"
                  >
                    Unarchive this record
                  </TextLink>
                </Text>
                {isRestoring ? <Spinner color="blue" /> : null}
              </Group>
            ) : (
              <Group>
                <Button
                  onClick={onSubmit}
                  variant="secondary"
                  fill
                  disabled={!dirty || isSending || isArchiving || isReopening}
                  loading={isSubmitting}
                  data-test="vendor-return-save-button"
                >
                  Save changes
                </Button>
                {dirty ? (
                  <Text body dimmed>
                    Unsaved changes
                  </Text>
                ) : null}
              </Group>
            )}

            <Group>
              {onSend ? (
                <Button variant="primary" fill loading={isSending} onClick={this.handleOpenDialog}>
                  Send to vendor
                </Button>
              ) : null}

              {onClose ? (
                <Button variant="primary" fill loading={isClosing} onClick={onClose}>
                  Mark as closed
                </Button>
              ) : null}

              {onArchive && (
                <Menu
                  target={({ active }) => (
                    <button
                      type="button"
                      className={cn('cr-button', { 'cr-button--active': active })}
                      data-test="vendor-return-actions-button"
                    >
                      <span className="cr-button__content">More actions</span>
                      <span className="cr-button__content">
                        <IconSmallChevronDown />
                      </span>
                    </button>
                  )}
                  light
                  noArrow
                  placement="bottom-end"
                >
                  {({ onClose: menuOnClose }) => (
                    <Fragment>
                      <MenuItem
                        href={null}
                        role="menuitem"
                        onClick={() => {
                          window.merchantos.print.printUrl({
                            url: `/API/Account/${window.merchantos.account.id}/DisplayTemplate/VendorReturn/${vendorReturnID}.html?template=VendorReturnPackingSlip&print=1`,
                            pageType: 'report',
                          });
                          menuOnClose();
                        }}
                        data-test="vendor-return-print-packing-slip-link"
                      >
                        <Text body color="gray">
                          Print packing slip
                        </Text>
                      </MenuItem>

                      {onReopen && (
                        <MenuItem
                          href={null}
                          role="menuitem"
                          onClick={() => (!isReopening ? onReopen({ onDone: menuOnClose }) : null)}
                          data-test="vendor-return-reopen-link"
                        >
                          <Group>
                            <Text body color="gray">
                              Reopen
                            </Text>
                            {isReopening ? <Spinner color="blue" /> : null}
                          </Group>
                        </MenuItem>
                      )}
                      {onArchive && (
                        <MenuItem
                          href={null}
                          role="menuitem"
                          onClick={() => (!isArchiving ? onArchive({ onDone: menuOnClose }) : null)}
                          data-test="vendor-return-archive-link"
                        >
                          <Group>
                            <Text body color="maple-200">
                              Archive
                            </Text>
                            {isArchiving ? <Spinner color="maple-200" /> : null}
                          </Group>
                        </MenuItem>
                      )}
                    </Fragment>
                  )}
                </Menu>
              )}
            </Group>
          </Group>
        ) : (
          <VendorReturnActionBarSkeleton />
        )}
      </PageActionBar>
    );
  }
}

export const VendorReturnActionBarSkeleton = () => (
  <div className="cr-flex cr-width-full">
    <div className="cr-flex-1">
      <Bone width="8rem" height="2.25rem" />
    </div>
    <Bone width="8rem" height="2.25rem" />
  </div>
);

export default VendorReturnActionBar;
